import { useState, useRef, useEffect } from "react";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { setCurrentUser } from "../store/actions/authActions.js";
import RiskRegistry from "./employees/RiskRegistry.tsx";
import RiskScoring from "./employees/RiskScoring.tsx";
import RiskAssessment from "./employees/RiskAssessment.tsx";
import RiskResponseRegistry from "./employees/RiskResponseRegistry.tsx";
import RiskResponseScoring from "./employees/RiskResponseScoring.tsx";
import { Logout } from "../context/Auth/AuthContext.js";
import EmployeeProfile from "./employees/EmployeeProfile.js";
import EmployeeKickoffPhase from "./employees/EmployeeKickoffPhase.js";
import { useTenant } from "../context/Tenant/TenantContext.js";
import { getBrandTheme } from "../utils/tenantTheme.js";
import { Loader } from "../components/Loader.js";

// ─── Shared theme definitions (same tokens as AdminWrapper) ───────────────────
const THEMES = {
  blueprint: {
    sb: '#1c3562', sbt: '#7d9cc4', sba: '#ffffff',
    sbab: 'rgba(255,255,255,0.12)', sbhov: 'rgba(255,255,255,0.07)',
    sbb: 'rgba(255,255,255,0.09)', sbsc: '#4a6a9e',
    tb: '#ffffff', tbt: '#0f172a', tbb: '#e8ecf0', tbst: '#64748b',
    cb: '#f1f5f9', pri: '#1d4ed8',
    avatarBg: '#eff6ff', avatarCol: '#2563eb',
  },
  obsidian: {
    sb: '#0c1220', sbt: '#4a5e78', sba: '#e2e8f0',
    sbab: 'rgba(255,255,255,0.08)', sbhov: 'rgba(255,255,255,0.04)',
    sbb: 'rgba(255,255,255,0.06)', sbsc: '#2a3a50',
    tb: '#ffffff', tbt: '#0f172a', tbb: '#f0f2f5', tbst: '#64748b',
    cb: '#fafafa', pri: '#3b82f6',
    avatarBg: '#eff6ff', avatarCol: '#2563eb',
  },
  daylight: {
    sb: '#f8fafc', sbt: '#5e7490', sba: '#0369a1',
    sbab: '#e0f2fe', sbhov: '#f0f9ff',
    sbb: '#e2e8f0', sbsc: '#94a3b8',
    tb: '#1e40af', tbt: '#ffffff', tbb: '#2552c8', tbst: 'rgba(255,255,255,0.72)',
    cb: '#eef2f7', pri: '#0284c7',
    avatarBg: '#eff6ff', avatarCol: '#2563eb',
  },
};

const PAGE_INFO = {
  'riskregistry':       { title: 'Risk Entry',         c0: 'Risk Registration', c1: 'Entry' },
  'riskscoring':        { title: 'Risk Rating',         c0: 'Risk Registration', c1: 'Rating' },
  'riskassessment':     { title: 'Risk Assessment',     c0: null, c1: null },
  'responseregistry':   { title: 'Response Entry',      c0: 'Risk Response',     c1: 'Entry' },
  'riskresponsescoring':{ title: 'Invite Comments',     c0: 'Risk Response',     c1: 'Invite Comments' },
  'employeeprofile':    { title: 'Profile',             c0: null, c1: null },
  'employeekickoffphase': { title: 'Kickoff Phase',    c0: null, c1: null },
};

// eslint-disable-next-line no-unused-vars -- theme picker hidden for now
function ThemeDot({ color, label, isActive, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      title={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 14, height: 14, borderRadius: '50%',
        background: color,
        cursor: 'pointer',
        flexShrink: 0,
        border: isActive ? '2px solid #fff' : '2px solid rgba(255,255,255,0.45)',
        boxShadow: isActive
          ? '0 0 0 2px rgba(255,255,255,0.85)'
          : hovered
          ? '0 0 0 2px rgba(255,255,255,0.55)'
          : 'none',
        transform: hovered ? 'scale(1.35)' : isActive ? 'scale(1.15)' : 'scale(1)',
        transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
      }}
    />
  );
}

function StepBadge({ n, color, sbColor }) {
  return (
    <div style={{
      width: 18, height: 18, minWidth: 18, borderRadius: '50%', background: color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 9, fontWeight: 800, color: '#fff', flexShrink: 0,
      boxShadow: `0 0 0 2px ${sbColor}`,
    }}>
      {n}
    </div>
  );
}

function EmployeeWrapper() {
  const { tenant, loading: tenantLoading } = useTenant();
  const location = useLocation();

  const [themeName, setThemeName] = useState(
    () => localStorage.getItem('riskTheme') || 'daylight'
  );
  const t = getBrandTheme(tenant?.branding, THEMES[themeName] || THEMES.daylight);
  // eslint-disable-next-line no-unused-vars -- theme picker hidden for now
  const applyTheme = (name) => {
    setThemeName(name);
    localStorage.setItem('riskTheme', name);
  };

  const [collapsed, setCollapsed] = useState(false);
  const sbW  = collapsed ? 68 : 240;
  const showL = !collapsed;

  const [regOpen,  setRegOpen]  = useState(true);
  const [respOpen, setRespOpen] = useState(true);

  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) setAvatarOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const seg = location.pathname.split('/').pop().toLowerCase();
  const pageInfo = PAGE_INFO[seg] ?? { title: 'Risk Register', c0: null, c1: null };

  const storedName = localStorage.getItem('name') || 'Employee';
  const initials = storedName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || 'E';

  const niStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 8px', borderRadius: 7, cursor: 'pointer',
    marginBottom: 1, textDecoration: 'none',
    background: isActive ? t.sbab : 'transparent',
  });
  const niText = (isActive) => ({
    fontSize: 13, color: isActive ? t.sba : t.sbt,
    fontWeight: isActive ? 600 : 400,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  });
  const topNavItem = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 10px', borderRadius: 8, cursor: 'pointer',
    marginBottom: 2, textDecoration: 'none',
    background: isActive ? t.sbab : 'transparent',
  });

  if (tenantLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader size="xl" />
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', height: '100vh', overflow: 'hidden',
      fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      WebkitFontSmoothing: 'antialiased',
      '--sb-hover': t.sbhov,
    }}>

      {/* ═══════════════════════════ SIDEBAR ════════════════════════════════ */}
      <div style={{
        width: sbW, minWidth: sbW, background: t.sb,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden', flexShrink: 0,
        borderRight: `1px solid ${t.sbb}`,
        transition: 'width 0.2s ease',
      }}>
        {/* Logo + collapse */}
        <div style={{
          height: 60, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '0 14px',
          borderBottom: `1px solid ${t.sbb}`, flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, overflow: 'hidden' }}>
            <div style={{
              width: 32, height: 32, minWidth: 32, borderRadius: 8,
              overflow: 'hidden', flexShrink: 0,
              background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <img src={tenant?.branding?.logoUrl || "/rsmLogo.png"} alt="logo" style={{ width: 28, height: 28, objectFit: 'contain' }} />
            </div>
            {showL && (
              <span style={{ color: t.sba, fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '-0.2px' }}>
                {tenant?.companyName || 'RSM Botswana'}
              </span>
            )}
          </div>
          <button
            onClick={() => setCollapsed(c => !c)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.sbt, padding: 4, borderRadius: 4, flexShrink: 0, fontSize: 13, lineHeight: 1 }}
          >
            {collapsed ? '→' : '←'}
          </button>
        </div>

        {/* Nav */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 8px' }}>

          {/* ── Risk Registration ── */}
          <div
            className="sb-item"
            onClick={() => { setRegOpen(o => !o); if (collapsed) setCollapsed(false); }}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 7, cursor: 'pointer', marginBottom: 1 }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: t.sbsc }}>
              <rect x="2" y="1" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M5 5h6M5 8h6M5 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {showL && (
              <>
                <span style={{ flex: 1, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: t.sbsc }}>
                  Risk Registration
                </span>
                <span style={{ fontSize: 11, color: t.sbsc }}>{regOpen ? '▾' : '▸'}</span>
              </>
            )}
          </div>

          {regOpen && (
            <div style={{ marginLeft: 10, paddingLeft: 14, borderLeft: `1px solid ${t.sbb}`, marginBottom: 4 }}>
              <NavLink to="riskregistry" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={1} color="#3b82f6" sbColor={t.sb}/>
                    {showL && <span style={niText(isActive)}>Entry</span>}
                  </div>
                )}
              </NavLink>
              <NavLink to="riskscoring" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={2} color="#f59e0b" sbColor={t.sb}/>
                    {showL && <span style={niText(isActive)}>Rating</span>}
                  </div>
                )}
              </NavLink>
            </div>
          )}

          {/* ── Risk Assessment (single link) ── */}
          <NavLink to="riskassessment" style={{ textDecoration: 'none' }}>
            {({ isActive }) => (
              <div className="sb-item" style={topNavItem(isActive)}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
                  style={{ flexShrink: 0, color: isActive ? t.sba : t.sbt }}>
                  <rect x="1" y="9" width="4" height="6" rx="1" fill="currentColor" opacity="0.5"/>
                  <rect x="6" y="5" width="4" height="10" rx="1" fill="currentColor" opacity="0.7"/>
                  <rect x="11" y="1" width="4" height="14" rx="1" fill="currentColor"/>
                </svg>
                {showL && <span style={niText(isActive)}>Risk Assessment</span>}
              </div>
            )}
          </NavLink>

          {/* ── Risk Response ── */}
          <div
            className="sb-item"
            onClick={() => { setRespOpen(o => !o); if (collapsed) setCollapsed(false); }}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 7, cursor: 'pointer', marginBottom: 1 }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: t.sbsc }}>
              <path d="M8 1L14 4v5c0 3-3 5-6 6-3-1-6-3-6-6V4l6-3z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.12"/>
            </svg>
            {showL && (
              <>
                <span style={{ flex: 1, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: t.sbsc }}>
                  Risk Response
                </span>
                <span style={{ fontSize: 11, color: t.sbsc }}>{respOpen ? '▾' : '▸'}</span>
              </>
            )}
          </div>

          {respOpen && (
            <div style={{ marginLeft: 10, paddingLeft: 14, borderLeft: `1px solid ${t.sbb}`, marginBottom: 4 }}>
              <NavLink to="responseregistry" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={1} color="#3b82f6" sbColor={t.sb}/>
                    {showL && <span style={niText(isActive)}>Entry</span>}
                  </div>
                )}
              </NavLink>
              <NavLink to="riskresponsescoring" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={2} color="#f59e0b" sbColor={t.sb}/>
                    {showL && <span style={{ ...niText(isActive), overflow: 'hidden', textOverflow: 'ellipsis' }}>Invite Comments</span>}
                  </div>
                )}
              </NavLink>
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════ MAIN PANEL ═════════════════════════════ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* Topbar */}
        <div style={{
          height: 60, background: t.tb, borderBottom: `1px solid ${t.tbb}`,
          display: 'flex', alignItems: 'center', padding: '0 24px', gap: 16, flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {pageInfo.c0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 1 }}>
                <span style={{ fontSize: 11, color: t.tbst, whiteSpace: 'nowrap' }}>{pageInfo.c0}</span>
                <span style={{ fontSize: 10, color: t.tbst }}>›</span>
                <span style={{ fontSize: 11, color: t.tbst, whiteSpace: 'nowrap' }}>{pageInfo.c1}</span>
              </div>
            )}
            <h1 style={{ fontSize: 17, fontWeight: 800, color: t.tbt, margin: 0, letterSpacing: '-0.4px', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {pageInfo.title}
            </h1>
          </div>

          {/* Theme picker — hidden for now
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
            background: 'rgba(255,255,255,0.13)',
            border: '1px solid rgba(255,255,255,0.22)',
            borderRadius: 100,
            padding: '5px 12px',
          }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.5px',
              textTransform: 'uppercase', color: t.tbt, opacity: 0.7,
              userSelect: 'none',
            }}>
              Theme
            </span>
            {[
              { key: 'blueprint', color: '#1c3562', label: 'Blueprint' },
              { key: 'obsidian',  color: '#0c1220', label: 'Obsidian'  },
              { key: 'daylight',  color: '#1e40af', label: 'Daylight'  },
            ].map(({ key, color, label }) => (
              <ThemeDot
                key={key}
                color={color}
                label={label}
                isActive={themeName === key}
                onClick={() => applyTheme(key)}
              />
            ))}
          </div>
          */}

          {/* Avatar */}
          <div ref={avatarRef} style={{ position: 'relative', flexShrink: 0 }}>
            <div
              onClick={() => setAvatarOpen(o => !o)}
              style={{
                width: 34, height: 34, borderRadius: '50%', background: t.avatarBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 800, color: t.avatarCol,
                cursor: 'pointer', border: `2px solid ${t.tbb}`, userSelect: 'none',
              }}
            >
              {initials}
            </div>
            {avatarOpen && (
              <div style={{
                position: 'absolute', top: 42, right: 0, width: 210,
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)', zIndex: 200, overflow: 'hidden',
              }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{storedName}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{localStorage.getItem('email')}</div>
                  <div style={{ marginTop: 6 }}>
                    <span style={{ display: 'inline-flex', padding: '2px 8px', background: '#eff6ff', borderRadius: 100, fontSize: 11, fontWeight: 700, color: '#2563eb' }}>
                      Employee
                    </span>
                  </div>
                </div>
                <div style={{ padding: '4px 0' }}>
                  <a href="/employee/employeeprofile"
                    className="avatar-menu-item"
                    style={{ display: 'block', padding: '8px 16px', fontSize: 13, color: '#374151', textDecoration: 'none' }}
                    onClick={() => setAvatarOpen(false)}
                  >
                    Profile
                  </a>
                  <button
                    onClick={() => { setAvatarOpen(false); Logout(); }}
                    className="avatar-menu-item"
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 16px', fontSize: 13, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <main style={{ flex: 1, overflowY: 'auto', padding: 24, background: t.cb }}>
          <Routes>
            <Route path="riskregistry"        element={<RiskRegistry />} />
            <Route path="riskscoring"         element={<RiskScoring />} />
            <Route path="responseregistry"    element={<RiskResponseRegistry />} />
            <Route path="riskresponsescoring" element={<RiskResponseScoring />} />
            <Route path="riskassessment"      element={<RiskAssessment />} />
            <Route path="employeekickoffphase" element={<EmployeeKickoffPhase />} />
            <Route path="employeeprofile"     element={<EmployeeProfile />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

const mapStateToProps    = (state)    => ({ user: state.userData.user });
const mapDispatchToProps = (dispatch) => ({ setUser: (user) => dispatch(setCurrentUser(user)) });

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeWrapper);
